import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ConversationSummaryStatesEnum} from '../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {ConversationInquiryModel} from '../../../../../../libs/shared/core/src/models/api/conversationInquiriesModel';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {RelatedKnowledgeReference} from '../../../../../models/api/relatedKnowledgeModel';
import {SimilarInquiry} from '../../../../../models/api/similarInquiriesModel';
import {manuallySummarizeConversationAsyncAction} from '../../../../../store/data/conversations/conversationsActions';
import {makeGetConversationById} from '../../../../../store/data/conversations/conversationsSelectors';
import {CopyButton} from '../../../../common/buttons/copyButton';

/*
 * Props.
 */

interface SynopsisInspectorProps {
  conversationId: number;
  onRequestClose?: () => void;

  inquiries: ReadonlyArray<ConversationInquiryModel>;
  similarInquiries: Record<ConversationInquiryModel['identifier'], ReadonlyArray<SimilarInquiry>> | undefined;
  relatedKnowledge:
    | Record<ConversationInquiryModel['identifier'], ReadonlyArray<RelatedKnowledgeReference>>
    | undefined;
}

/*
 * Styles.
 */

const StyledContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 300px;
`;

const StyledHeader = styled.header`
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  grid-template-areas: 'close title . controls';
  align-items: center;
  padding: 8px 16px;
  grid-gap: 10px;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  margin-left: -8px;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
`;

const StyledControlsDiv = styled.div`
  grid-area: controls;
`;

const StyledDataPre = styled.pre`
  flex-grow: 1;
  margin: 0;
  overflow-y: auto;
  white-space: pre-wrap;
  font-size: ${fontSizes.small};
  user-select: text;
  cursor: text;
  padding: 10px 20px;
  background-color: ${(p) => p.theme.greys.white};
  border-radius: 16px;
  margin: 0 16px 16px 16px;
`;

/*
 * Component.
 */

export const SynopsisInspector: FC<SynopsisInspectorProps> = ({
  conversationId,
  onRequestClose,
  inquiries,
  similarInquiries,
  relatedKnowledge,
}) => {
  const getConversation = useMemo(() => makeGetConversationById(conversationId), [conversationId]);
  const conversation = useAppSelector(getConversation);

  const dispatch = useAppDispatch();

  const data = JSON.stringify({inquiries, similarInquiries, relatedKnowledge}, null, 2);

  // This should not happen.
  if (!conversation) {
    return null;
  }

  const isRegeneratingGraph = conversation.summaryState === ConversationSummaryStatesEnum.LOADING;

  const onRegenerateClick = () => {
    if (!isRegeneratingGraph) {
      dispatch(manuallySummarizeConversationAsyncAction(conversationId));
    }
  };

  return (
    <StyledContentDiv>
      <StyledHeader>
        <StyledTitleDiv>
          <Trans id="synopsis-inspector-title">Graph inspector</Trans>
        </StyledTitleDiv>
        <StyledControlsDiv>
          <ButtonGroup>
            <IconButton
              iconName="repeatCircleSmall"
              label={
                isRegeneratingGraph ? (
                  <Trans id="synopsis-inspector-regenerating">Regenerating…</Trans>
                ) : (
                  <Trans id="synopsis-inspector-regenerate">Regenerate</Trans>
                )
              }
              onClick={onRegenerateClick}
              isDisabled={isRegeneratingGraph}
            />
            <CopyButton copyValue={data} />
            <IconButton
              iconName="closeSmall"
              label={<Trans id="synopsis-inspector-close">Close</Trans>}
              onClick={onRequestClose}
            />
          </ButtonGroup>
        </StyledControlsDiv>
      </StyledHeader>
      <StyledDataPre>{data}</StyledDataPre>
    </StyledContentDiv>
  );
};
